import { differenceInCalendarDays, format, startOfDay } from 'date-fns';

/**
 * Turning trade rows into what the screens draw. Three kinds arrive: already
 * paired positions (`open_time`/`close_time` on one row), the metrics
 * endpoint's `recent_trades` (one row per closed trade, only its `time`), and
 * leg ledgers that need an opening and a closing deal matched up.
 *
 * Shared because both ends need it: Account Metrics (recent trades, the daily
 * calendar) and Trade History (the full table and its selected trade).
 */

/** "CADJPY.pro" is the broker's symbol; the flag and the eye want "CADJPY". */
const bareSymbol = (symbol) => String(symbol ?? '').split('.')[0];

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

/** A missing fee is nothing owed, not a NaN poisoning the whole P&L. */
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Money adds up in cents — otherwise a fee lands as -0.30000000000000004. */
const round2 = (value) => Math.round(value * 100) / 100;

/**
 * Each platform spells the side its own way — "Sell" (MT5, cTrader), "SELL"
 * (DXtrade), "Buy" (TradeLocker) — and both the table's buy/sell tint and the
 * inversion below compare against one spelling, so they all become it.
 */
const asSide = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  if (text.startsWith('B')) return 'Buy';
  if (text.startsWith('S')) return 'Sell';
  return '—';
};

const flipSide = (side) => (side === 'Buy' ? 'Sell' : side === 'Sell' ? 'Buy' : '—');

const durationBetween = (open, close) => {
  if (!isValidDate(open) || !isValidDate(close)) return '—';
  const minutes = Math.max(0, Math.round((close - open) / 60000));
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${String(minutes % 60).padStart(2, '0')}m` : `${minutes}m`;
};

/**
 * The ledger arrives in one of four shapes depending on the platform, keyed
 * here by the platform slug the URL and the account both carry (`?platform=mt5`):
 *
 *   MT5          deals,  `entry` In/Out,              paired on `position`
 *   DXtrade      orders, `entry` In/Out,              paired on `position`
 *   cTrader      deals,  `deal_type` Open/Close,      paired on `position`
 *   TradeLocker  trades, `positionStatus` OPEN/CLOSE, paired on `positionId`
 *
 * `matches` still runs once the ledger is picked — it is what keeps the rows
 * that aren't trades out of the list (an MT5 balance operation carries no
 * In/Out `entry`, a cTrader one no Open/Close `deal_type`). It doubles as the
 * fallback picker for a caller with no platform to hand, which is why MT5 and
 * DXtrade — the two In/Out ledgers — still split on the `deal` ticket only MT5
 * sends. It has to be the absence of one that picks DXtrade rather than the
 * presence of an `order`: MT5 rows carry an `order` ticket too.
 *
 * `pnl` is the closing row's own profit and `fees` is what one leg costs, kept
 * apart because commission is charged per leg — a position's P&L is the close's
 * profit plus both legs' fees. A platform that already nets it out says so.
 */
const LEDGERS = {
  mt5: {
    // The only In/Out ledger with a `deal` ticket on the row.
    matches: (row) => (row?.entry === 'In' || row?.entry === 'Out') && row?.deal != null,
    isOpen: (row) => row.entry === 'In',
    pairKey: (row) => row.position,
    id: (row) => row.deal,
    symbol: (row) => row.symbol,
    side: (row) => asSide(row.action),
    // `||`, not `??`: a closing deal reporting `volumeClosed: 0` means the
    // broker left the field unfilled, not that nothing closed.
    lots: (row) => num(row.volumeClosed || row.volume),
    pnl: (row) => num(row.profit),
    fees: (row) => num(row.commission) + num(row.swap),
    at: (row) => new Date(row.date),
    price: (row) => row.price,
    // The platform's own word for how the deal was placed (Mobile, Expert).
    result: (row) => row.reason ?? '—',
  },
  dxtrade: {
    // The same In/Out legs as MT5, but ticketed by `order`, no `deal`.
    matches: (row) =>
      (row?.entry === 'In' || row?.entry === 'Out') && row?.deal == null && row?.order != null,
    isOpen: (row) => row.entry === 'In',
    pairKey: (row) => row.position,
    id: (row) => row.order,
    symbol: (row) => row.symbol,
    // "SELL" / "BUY" here, not MT5's "Sell" / "Buy".
    side: (row) => asSide(row.action),
    lots: (row) => num(row.volume),
    // `new_profit` is the figure the platform settles on; `profit` is what the
    // order reported. They agree on a completed order — take the settled one.
    pnl: (row) => num(row.new_profit ?? row.profit),
    fees: (row) => num(row.commission) + num(row.swap),
    at: (row) => new Date(row.date),
    price: (row) => row.price,
    // No placement reason on a DXtrade order — `type` and `status` are the
    // order's own mechanics, not how a person placed it.
    result: () => '—',
  },
  ctrader: {
    // Legs are `deal_type` Open/Close, which also keeps the account's balance
    // and deposit deals out of the trade list.
    matches: (row) => row?.deal_type === 'Open' || row?.deal_type === 'Close',
    isOpen: (row) => row.deal_type === 'Open',
    pairKey: (row) => row.position,
    id: (row) => row.deal_id,
    symbol: (row) => row.symbol,
    side: (row) => asSide(row.trade_side),
    // `volume` is the lot count; the `lot_size` beside it is the contract size,
    // the way MT5 sends `symbolContractSize` next to its own `volume`.
    lots: (row) => num(row.volume),
    // `profit` is in the account's currency. `new_profit` is the same figure in
    // cents (-3.38 → -338), which is how `balance` arrives too — not this.
    pnl: (row) => num(row.profit),
    // No swap on a cTrader deal; it settles separately.
    fees: (row) => num(row.commission),
    at: (row) => new Date(row.date),
    price: (row) => row.price,
    // No placement reason on a cTrader deal — `order_label` is the trader's own.
    result: () => '—',
  },
  tradelocker: {
    matches: (row) => row?.positionStatus === 'OPEN' || row?.positionStatus === 'CLOSE',
    isOpen: (row) => row.positionStatus === 'OPEN',
    pairKey: (row) => row.positionId,
    id: (row) => row.tradeId,
    symbol: (row) => row.instrument,
    side: (row) => asSide(row.side),
    lots: (row) => num(row.lots),
    // `netPnl`, not `profit`: it carries both legs' fees, and it is the figure
    // `profit_chart` plots, so these rows add up to the curve.
    pnl: (row) => num(row.netPnl),
    // Already inside `netPnl` — charging `executionFee` again would double it.
    fees: () => 0,
    // `tradeDateTime` has no zone, so it reads as the broker's own clock in
    // every viewer's timezone. `tradeTime` is the epoch fallback.
    at: (row) => new Date(row.tradeDateTime ?? Number(row.tradeTime)),
    price: (row) => row.price,
    // No placement reason on a TradeLocker trade.
    result: () => '—',
  },
};

/**
 * `?platform=…` — as the URL spells it and as an account carries it — to the
 * ledger that reads that platform's rows. An MT4 account's history comes back
 * as the same deal rows as MT5's.
 */
const ledgerFor = (platform) => {
  const slug = String(platform ?? '').trim().toLowerCase();
  return LEDGERS[slug === 'mt4' ? 'mt5' : slug] ?? null;
};

/**
 * Which ledger reads these rows. The platform the page was asked for decides
 * it; sniffing the rows is the fallback, for a caller with no platform to hand
 * and for a slug spelled some way `LEDGERS` doesn't know yet.
 */
const pickLedger = (list, platform) =>
  ledgerFor(platform) ??
  Object.values(LEDGERS).find((candidate) => list.some(candidate.matches)) ??
  null;

const newestFirst = (a, b) => b.closedAt - a.closedAt;

/** One row per position, both ends on it — no legs to pair. */
const isPaired = (row) => row?.open_time != null && row?.close_time != null;

const fromPaired = (rows) =>
  rows
    .filter(isPaired)
    .map((row) => {
      const closedAt = new Date(row.close_time);
      if (!isValidDate(closedAt)) return null;
      const opened = new Date(row.open_time);
      const openedAt = isValidDate(opened) ? opened : null;

      return {
        id: row.position_id,
        // The row is the position, so the two are the same ticket here.
        position: row.position_id,
        symbol: bareSymbol(row.symbol),
        // Already the position's own side, so nothing to invert.
        side: asSide(row.side),
        lots: num(row.volume),
        // `net_profit` carries swap and commission, and it is the figure that
        // sums to the statistics' own `net_profit`. `profit` alone does not.
        pnl: round2(num(row.net_profit ?? row.profit)),
        closedAt,
        openedAt,
        date: format(closedAt, 'd MMM yyyy'),
        open: openedAt ? format(openedAt, 'HH:mm') : '—',
        close: format(closedAt, 'HH:mm'),
        heldDays: openedAt
          ? differenceInCalendarDays(startOfDay(closedAt), startOfDay(openedAt))
          : 0,
        time: format(closedAt, 'HH:mm'),
        entry: row.open_price ?? null,
        exit: row.close_price ?? null,
        duration: openedAt ? durationBetween(openedAt, closedAt) : '—',
        // No placement reason on a paired row.
        result: '—',
      };
    })
    .filter(Boolean)
    .sort(newestFirst);

/**
 * `/v2/account-metrics/accounts/<login>/`'s `recent_trades` — the public
 * page's row: `symbol`, `side`, `lots`, `time`, `profit`, `exit_reason`. No
 * ticket and no open time, so no duration either.
 * NEEDS PINNING: only ever seen empty on this endpoint; the shape is read off
 * `/v2/account/public/`'s `recent_trades`, which the same backend builds.
 */
const isSummary = (row) =>
  row?.time != null &&
  row?.profit !== undefined &&
  !isPaired(row) &&
  // A ledger leg can carry a `time` of its own; those are read as legs.
  !Object.values(LEDGERS).some((ledger) => ledger.matches(row));

const fromSummary = (rows) =>
  rows
    .filter(isSummary)
    .map((row, i) => {
      const closedAt = new Date(row.time);
      if (!isValidDate(closedAt)) return null;
      return {
        // Several trades can share a symbol and a second, so the place in the
        // list is what tells them apart.
        id: row.position_id ?? row.id ?? i,
        position: row.position_id ?? null,
        symbol: bareSymbol(row.symbol),
        side: asSide(row.side),
        lots: num(row.lots),
        pnl: round2(num(row.profit)),
        closedAt,
        openedAt: null,
        date: format(closedAt, 'd MMM yyyy'),
        open: '—',
        close: format(closedAt, 'HH:mm'),
        heldDays: 0,
        time: format(closedAt, 'HH:mm'),
        entry: null,
        exit: null,
        duration: '—',
        result: row.exit_reason ?? '—',
      };
    })
    .filter(Boolean)
    .sort(newestFirst);

/**
 * Rows into closed trades, newest first. `platform` is the account's own slug
 * — the `?platform=` the page was opened with — and picks which ledger reads
 * the rows.
 *
 * Paired rows map straight across whatever the platform. For a leg ledger the
 * side comes from the opening leg — a short position is *closed* by a Buy, so
 * reading the close would report every trade backwards.
 */
export const closedTrades = (rows = [], platform) => {
  const list = Array.isArray(rows) ? rows : [];
  if (list.some(isPaired)) return fromPaired(list);
  if (list.some(isSummary)) return fromSummary(list);

  const ledger = pickLedger(list, platform);
  if (!ledger) return [];

  const openings = new Map();
  for (const row of list) {
    if (ledger.matches(row) && ledger.isOpen(row)) openings.set(ledger.pairKey(row), row);
  }

  return list
    .filter((row) => ledger.matches(row) && !ledger.isOpen(row))
    .map((row) => {
      const closedAt = ledger.at(row);
      if (!isValidDate(closedAt)) return null;

      const opening = openings.get(ledger.pairKey(row));
      const openedAtRaw = opening ? ledger.at(opening) : null;
      const openedAt = isValidDate(openedAtRaw) ? openedAtRaw : null;

      return {
        id: ledger.id(row),
        // The ticket both legs share — what a click on either of them means.
        position: ledger.pairKey(row),
        symbol: bareSymbol(ledger.symbol(row)),
        // Opened before the window the API returned: invert the close instead.
        side: opening ? ledger.side(opening) : flipSide(ledger.side(row)),
        lots: ledger.lots(row),
        // Both legs' fees: commission is charged opening and closing, and the
        // opening leg is the only row carrying its own.
        pnl: round2(ledger.pnl(row) + ledger.fees(row) + (opening ? ledger.fees(opening) : 0)),
        closedAt,
        openedAt,
        // The date a trade is filed under is the day it closed.
        date: format(closedAt, 'd MMM yyyy'),
        open: openedAt ? format(openedAt, 'HH:mm') : '—',
        close: format(closedAt, 'HH:mm'),
        /**
         * Whole days between the two, so a held-overnight trade can say so —
         * without it the columns read "08:40 → 07:10", the open after the close.
         */
        heldDays: openedAt
          ? differenceInCalendarDays(startOfDay(closedAt), startOfDay(openedAt))
          : 0,
        // Same clock as `close`; Account Metrics' lists label it `time`.
        time: format(closedAt, 'HH:mm'),
        entry: opening ? ledger.price(opening) : null,
        exit: ledger.price(row),
        duration: openedAt ? durationBetween(openedAt, closedAt) : '—',
        result: ledger.result(row),
      };
    })
    .filter(Boolean)
    .sort(newestFirst);
};

/**
 * A response row's own ticket, and the position it belongs to — read through
 * the same ledger `closedTrades` uses, so the two agree. Trade History draws
 * the raw rows and keys them on `id`; `position` is what both legs of a
 * position share, so a click on either one points at the same trade.
 *
 * Both are null for a row this platform's ledger doesn't count as a trade — a
 * balance operation, a deposit — which the caller can fall back on the index
 * for.
 */
export const rowKeys = (row, platform) => {
  if (isPaired(row)) return { id: row.position_id, position: row.position_id };

  const ledger = pickLedger([row], platform);
  if (!ledger || !ledger.matches(row)) return { id: null, position: null };
  return { id: ledger.id(row), position: ledger.pairKey(row) };
};
